/** @format */

import {RASA_HOST} from "../../constants";
import {NeuroDetailsType} from "../constants";
import {Storage} from "../utils/Storage";

import { ENVIRONMENT } from "../../constants";
import { CircumstancesType } from "../../constants"; // make sure this is available
import { getSomeProfileDataForRasa } from "../utils/profile.utils";
import { updatePassportAndWellbeingFormData } from "../utils/passport.utils";
import {updateSlotArrayForUnCheckedCircumstances} from "../screens/Chat/utils";

interface EventMetadata {
    model_id: string;
    assistant_id: string;
}

interface Event {
    event: string;
    timestamp: number;
    metadata: EventMetadata;
    name?: string;
    policy?: any;
    confidence?: number | null;
    action_text?: string | null;
    hide_rule_turn?: boolean;
}

interface LatestMessage {
    intent: any;
    entities: any[];
    text: string | null;
    message_id: string | null;
    metadata: any;
}

interface ActiveLoop {
    // Don't have the properties yet
}

export interface RasaContext {
    sender_id: string;
    slots: {
        first_name: string;
        given_name: string; //first name
        profile_nickname: string;
        profile_support_network: string;
        profile_support_services: string;
        profile_contacts: string;
        profile_work: string;
        profile_mood: string;
        "custom:circumstances": string;
        profile_university: string;
        profile_course: string;
        wellbeing_impact: null;
        session_started_metadata: null;
        passport_strategies: string;
        passport_network: string;
        passport_signs: string;
        passport_triggers: string;
        passport_impact: string;
        passport_aspects: string;
        mh_issues_current: string;
        mh_issues_past: string;
        mh_challenges: string;
        neuro_details: NeuroDetailsType[];
        neuro_diagnosed: string;
        neuro_challenges: string;
        wb_current: string;
        wb_past: string;
        wb_challenges: string;
        disabilty_details: string;
        disabilty_difficulties: string;
        disabilty_challenges: string;
    };
    latest_message: LatestMessage;
    latest_event_time: number;
    followup_action: null;
    paused: boolean;
    events: Event[];
    latest_input_channel: string | null;
    active_loop: ActiveLoop;
    latest_action: {
        action_name: string;
    };
    latest_action_name: string;
}


export class RasaUtils {
    host = RASA_HOST;

    constructor(host?: string) {
        this.host = host || this.host;
    }

    async updateRasaContext(chatId: string, body: any, accessToken: string): Promise<RasaContext> {
        const response = await fetch(
          `${this.host}/conversations/${chatId}/tracker/events?include_events=NONE`,
          {
              method: "POST",
/*              headers: {
                  'Authorization': 'Bearer ' + accessToken,
              },*/
              body: JSON.stringify(body),
          }
        );
        const data = await response.json();
        return data;
    }

    async retrieveContext(chatId: string, accessToken: string): Promise<RasaContext> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // Timeout after 8 seconds
        try {
            const response = await fetch(
              `${this.host}/conversations/${chatId}/tracker`,
              {
/*                  headers: {
                      'Authorization': `Bearer ${accessToken}`,
                  },*/
                  signal: controller.signal,  // Attach signal for timeout
              }
            );

            clearTimeout(timeoutId); // Clear timeout if request succeeds

            if (!response.ok) {
                console.error(`Error: Received status ${response.status} from Rasa server`);
                return this.getEmptyContext(chatId);
            }

            // Ensure valid JSON response
            try {
                const data: RasaContext = await response.json();
                return data;
            } catch (jsonError) {
                console.error("Error parsing JSON response:", jsonError);
                return this.getEmptyContext(chatId);
            }
        } catch (error: any) {
            if (error.name === "AbortError") {
                console.error("Request timeout: Rasa server took too long to respond");
            } else {
                console.error("Error retrieving context:", error);
            }

            return this.getEmptyContext(chatId); // Return an empty context on failure
        }
    }

    /**
     * Returns an empty RasaContext object
     */
    private getEmptyContext(chatId: string): RasaContext {
        return {
            sender_id: chatId,
            slots: {
                first_name: "",
                given_name: "",
                profile_nickname: "",
                profile_support_network: "",
                profile_support_services: "",
                profile_contacts: "",
                profile_work: "",
                profile_mood: "",
                "custom:circumstances": "",
                profile_university: "",
                profile_course: "",
                wellbeing_impact: null,
                session_started_metadata: null,
                passport_strategies: "",
                passport_network: "",
                passport_signs: "",
                passport_triggers: "",
                passport_impact: "",
                passport_aspects: "",
                mh_issues_current: "",
                mh_issues_past: "",
                mh_challenges: "",
                neuro_details: [],
                neuro_diagnosed: "",
                neuro_challenges: "",
                wb_current: "",
                wb_past: "",
                wb_challenges: "",
                disabilty_details: "",
                disabilty_difficulties: "",
                disabilty_challenges: "",
            },
            latest_message: {
                text: "",
                intent: {name: "", confidence: 0},  // Ensure default intent structure
                entities: [],
                message_id: "",
                metadata: {},
            },
            latest_event_time: 0,
            followup_action: null,
            paused: false,
            events: [],
            latest_input_channel: null,
            active_loop: {},  // Set as an empty object if undefined
            latest_action: {action_name: ""},
            latest_action_name: "",
        };
    }

    async saveContext(context: RasaContext): Promise<any> {
        await Storage.setValue("rasaContext", JSON.stringify(context));
        await this.setWellbeingFormValues(context);
    }

    async setWellbeingFormValues(rasaContext: RasaContext) {
        try {
            const {
                first_name,
                wellbeing_impact,
                session_started_metadata,
                neuro_details,
                ...resRasaData
            } = rasaContext.slots;
            const data: Record<string, any> = resRasaData;

            // Convert neuro_details to an array of NeuroDetailsType
            const formattedNeuroDetails: NeuroDetailsType[] =
              typeof neuro_details === "string"
                ? neuro_details.split(",").map(detail => detail.trim())
                : Array.isArray(neuro_details)
                  ? neuro_details
                  : [];

            // Store valid values only if not already stored
            for (const [key, value] of Object.entries(data)) {
                if (
                  key.startsWith("testing") ||
                  value === null ||
                  value === undefined ||
                  typeof value !== "string" ||
                  value.toLowerCase() === "deactivate" ||
                  value.toLowerCase() === "stop" ||
                  value.startsWith("/welcome")
                ) {
                    continue; // Skip invalid values
                }

                try {
                    const storedValue = await Storage.getValue(key);
                    if (!storedValue) {
                        await Storage.setValue(key, value);
                    }
                } catch (error) {
                    console.error(`Error storing key: ${key}`, error);
                }
            }

            // Save formattedNeuroDetails to local storage
            await Storage.setValue("neuro_details", formattedNeuroDetails);
        } catch (error) {
            console.error("Error in setWellbeingFormValues:", error);
        }
    }

    static async buildRasaContextSlots(userId: string, email: string, idToken: string) {
        const timeStamp = Date.now();
        const storedDataKey = "custom:circumstances";

        const _data = await Storage.getStorage();
        const formStorageData = updatePassportAndWellbeingFormData(_data);
        const circumstancesValue = (await Storage.getValue(storedDataKey)) as string ?? "";
        const circumstancesObjectMap = circumstancesValue
          .split(",")
          .filter((val) => !!val) as CircumstancesType[];

        // Wellbeing form values
        let slotArray = Object.entries(formStorageData)
          .filter(([_, value]) => `${value}` !== "null")
          .map(([key, value]) => ({
              event: "slot",
              name: key,
              value: `${value}`,
              timestamp: timeStamp,
          }));

        // Overwrite unchecked circumstances with "N/A"
        slotArray = updateSlotArrayForUnCheckedCircumstances(slotArray, circumstancesObjectMap);

        // Profile form values
        const getSomeProfileFormDataFromStorage = getSomeProfileDataForRasa(_data, circumstancesObjectMap);
        const profileFormData = {
            ...getSomeProfileFormDataFromStorage,
            ["circumstances"]: circumstancesObjectMap,
        };

        const profileSlots = Object.entries(profileFormData).map(([key, value]) => ({
            event: "slot",
            name: key === "given_name" ? "first_name" : key,
            value: `${value}`,
            timestamp: timeStamp,
        }));

        // Extra metadata
        const extraSlots = [];

        if (idToken) {
            extraSlots.push({
                event: "slot",
                name: "token_id",
                value: idToken,
                timestamp: timeStamp,
            });
        }

        if (email) {
            const userDomain = email.split("@")[1];
            extraSlots.push({
                event: "slot",
                name: "user_domain",
                value: userDomain,
                timestamp: timeStamp,
            });
        }

        if (ENVIRONMENT) {
            extraSlots.push({
                event: "slot",
                name: "environment",
                value: ENVIRONMENT,
                timestamp: timeStamp,
            });
        }

        return [...slotArray, ...profileSlots, ...extraSlots].filter(
          (item) => item.value !== null && item.value.trim() !== ""
        );
    }
}
